import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Compass, TrendingUp, Mail, Settings2, ChevronDown, Phone } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QuizAnalytics = ({ headers }) => {
  const [a, setA] = useState(null);
  const [calls, setCalls] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/quiz/analytics`, { headers }).then((r) => setA(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/calls/analytics`, { headers }).then((r) => setCalls(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/quiz/nudge-settings`, { headers }).then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const saveCfg = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/quiz/nudge-settings`, cfg, { headers });
      toast.success('Reminder settings saved');
    } catch (e) { toast.error('Save failed'); }
    setSaving(false);
  };

  if (!a) return null;

  const cards = [
    { icon: Compass, label: 'Quiz starts', value: a.starts, sub: 'Visitors who began the quiz' },
    { icon: TrendingUp, label: 'Completions', value: a.completions, sub: `${a.completion_rate}% of starts finished` },
    { icon: Mail, label: 'Leads captured', value: a.leads, sub: `${a.lead_rate}% of starts left an email` },
  ];

  const callMap = {};
  (calls && calls.daily ? calls.daily : []).forEach((d) => { callMap[d.date] = d.clicks || 0; });
  const dates = Array.from(new Set([
    ...(a.daily || []).map((d) => d.date),
    ...(calls && calls.daily ? calls.daily.map((d) => d.date) : []),
  ])).filter(Boolean).sort();
  const quizMap = {};
  (a.daily || []).forEach((d) => { quizMap[d.date] = d; });
  const chartData = dates.map((date) => ({
    date: date.slice(5), // MM-DD
    starts: (quizMap[date] && quizMap[date].starts) || 0,
    completions: (quizMap[date] && quizMap[date].completions) || 0,
    calls: callMap[date] || 0,
  }));

  return (
    <div className="mb-8" data-testid="quiz-analytics">
      <div className="flex items-center gap-2 mb-3">
        <Compass className="h-5 w-5 text-[#B87333]" />
        <h2 className="text-lg font-bold text-[#003456]">Machine-selector quiz (last 30 days)</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100" data-testid={`quiz-stat-${c.label.split(' ')[0].toLowerCase()}`}>
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">{c.label}</p>
              <c.icon className="h-5 w-5 text-[#CE8946]" />
            </div>
            <p className="text-3xl font-bold text-[#003456] mt-1">{c.value}</p>
            <p className="text-xs text-gray-400 mt-1">{c.sub}</p>
          </div>
        ))}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100" data-testid="call-stat">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">Click-to-call</p>
            <Phone className="h-5 w-5 text-[#CE8946]" />
          </div>
          <p className="text-3xl font-bold text-[#003456] mt-1">{calls ? calls.clicks : 0}</p>
          <p className="text-xs text-gray-400 mt-1">Visitors who tapped a phone number</p>
        </div>
      </div>

      {/* Daily trend */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 mt-4" data-testid="quiz-trend-chart">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-[#003456]">Daily activity</p>
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#CE8946] inline-block" /> Starts</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#003456] inline-block" /> Completions</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#0E9AA7] inline-block" /> Call taps</span>
          </div>
        </div>
        {chartData.length === 0 ? (
          <p className="text-sm text-gray-400 py-10 text-center">No quiz activity yet. The trend will appear here as visitors take the quiz.</p>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 12 }} />
              <Line type="monotone" dataKey="starts" stroke="#CE8946" strokeWidth={2.5} dot={{ r: 2 }} activeDot={{ r: 5 }} />
              <Line type="monotone" dataKey="completions" stroke="#003456" strokeWidth={2} dot={{ r: 2 }} />
              <Line type="monotone" dataKey="calls" stroke="#0E9AA7" strokeWidth={2} dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Abandoned-quote reminder settings */}
      {cfg && (
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 mt-4" data-testid="nudge-settings">
          <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between" data-testid="nudge-settings-toggle">
            <span className="flex items-center gap-2 text-sm font-semibold text-[#003456]"><Settings2 className="h-4 w-4 text-[#B87333]" /> Abandoned-quote reminder</span>
            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
          {open && (
            <div className="mt-4 space-y-4">
              <div className="flex items-center gap-6 flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={cfg.quiz_nudge_enabled} onChange={(e) => setCfg({ ...cfg, quiz_nudge_enabled: e.target.checked })} className="h-4 w-4 accent-[#CE8946]" data-testid="nudge-enabled" />
                  <span className="text-sm font-medium text-gray-700">Send reminders automatically</span>
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  Wait
                  <input type="number" min={0} value={cfg.quiz_nudge_delay_minutes} onChange={(e) => setCfg({ ...cfg, quiz_nudge_delay_minutes: parseInt(e.target.value) || 0 })} className="w-24 px-2 py-1.5 border border-gray-300 rounded-lg" data-testid="nudge-delay" />
                  minutes before nudging
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email subject</label>
                <input value={cfg.quiz_nudge_subject} onChange={(e) => setCfg({ ...cfg, quiz_nudge_subject: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#CE8946]" data-testid="nudge-subject" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email heading</label>
                <input value={cfg.quiz_nudge_heading} onChange={(e) => setCfg({ ...cfg, quiz_nudge_heading: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#CE8946]" data-testid="nudge-heading" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email message <span className="text-gray-400 font-normal">(use {'{recommendation}'} to insert the recommended tier)</span></label>
                <textarea value={cfg.quiz_nudge_body} onChange={(e) => setCfg({ ...cfg, quiz_nudge_body: e.target.value })} rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#CE8946]" data-testid="nudge-body" />
              </div>
              <button onClick={saveCfg} disabled={saving} className="bg-[#003456] hover:bg-[#023F63] disabled:opacity-60 text-white font-semibold px-6 py-2.5 rounded-lg transition-colors" data-testid="nudge-save">{saving ? 'Saving…' : 'Save reminder settings'}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuizAnalytics;
